/**
 * Copyright (c) 2018-present, Didi, Inc.
 * All rights reserved.
 *
 * @author Cory(kuanghongrui@didichuxing.com)
 *
 * @file The class of PluginSet
 */

import { IPlugin } from 'com/didichuxing/plugin/IPlugin';
import { IPluginCollection } from 'com/didichuxing/plugin/IPluginCollection';

export default class PluginSet implements IPluginCollection {

    /**
     * The plugins set
     */
    private plugins: IPlugin[];

    /**
     * @constructor
     */
    constructor() {
        this.plugins = [];
    }

    /**
     * @override
     * @inheritDoc
     * @param {IPlugin} plugin
     * @returns {boolean}
     */
    public register(plugin: IPlugin): boolean {
        if (this.contains(plugin)) {
            return false;
        } else {
            plugin.install();
            this.plugins.push(plugin);
            return true;
        }
    }

    /**
     * @override
     * @inheritDoc
     * @param {string | IPlugin} plugin
     * @returns {boolean}
     */
    public contains(plugin: string | IPlugin): boolean {
        if (typeof plugin === 'string') { // plugin name
            for (let i: number = 0, j: number = this.size(); i < j; ++i) {
                if (this.plugins[i].getName() === plugin) {
                    return true;
                }
            }
            return false;
        } else { // plugin object
            for (let i: number = 0, j: number = this.size(); i < j; ++i) {
                if (this.plugins[i].getName() === plugin.getName()) {
                    return true;
                }
            }
            return false;
        }
    }

    /**
     * @override
     * @inheritDoc
     * @returns {number}
     */
    public size(): number {
        return this.plugins.length;
    }

    /**
     * 删除指定plugin对象。
     * @param {IPlugin} plugin
     * @returns {IPlugin | undefined}
     */
    private removePlugin(plugin: IPlugin): IPlugin | undefined {
        if (this.contains(plugin)) {
            this.plugins.splice(this.plugins.indexOf(plugin), 1);
            return plugin;
        }
        return;
    }

    /**
     * @override
     * @inheritDoc
     * @param {string | IPlugin} plugin
     * @returns {IPlugin | null}
     */
    public unregister(plugin: string | IPlugin): IPlugin | null {
        if (this.contains(plugin)) { // contains
            if (typeof plugin === 'string') { // plugin name, delete plugin via plugin full name
                for (let i: number = 0, j: number = this.plugins.length; i < j; ++i) {
                    const pluginItem: IPlugin = this.plugins[i];
                    if (pluginItem.getName() === plugin) {
                        return this.removePlugin(pluginItem) ? pluginItem : null;
                    }
                }
                return null;
            } else if (this.removePlugin(plugin)) { // delete plugin object directly
                return plugin;
            }
            return null;
        }
        return null;
    }

    /**
     * @override
     * @inheritDoc
     */
    public unregisterAll(): void {
        while (this.plugins.length) {
            const plugin: IPlugin | undefined = this.plugins.pop();
            if (plugin) {
                plugin.uninstall();
            }
        }
    }

    /**
     * @override
     * @inheritDoc
     * @returns {IPlugin[]}
     */
    public toArray(): IPlugin[] {
        return this.plugins;
    }
}
